import { MarketplaceContextShape } from 'context/Marketplace'
import { RARITIES } from 'core/constants'
import { AssetType } from 'core/logic/asset/asset.types'
import { useCallback, useMemo, useState } from 'react'
import {
  Filter,
  MarketplaceAssetFilter,
  MarketplaceFilter,
  MarketplaceOrderFilter,
  OptionsShape,
  TraitType,
} from './AssetFilters.types'
import { debounce, flatMap, isObject, keys } from 'lodash'
import { ATTRIBUTES_VISIBILITY } from './AssetFilters.data'
import { logDevError } from 'context/ErrorReporting'
import useRouteGalleryOrDefault from 'openspace/hooks/useRoutePathGallery'
import useFirestoreWatch from '@onepercentio/one-ui/hooks/useFirestoreWatch'
import { getFilterSnapshot } from 'core/modules/firebase/service'
import { getKeysAtLevel } from 'core/helpers/utils'

export function useFilteredAssets<T extends AssetType>(
  filter: Filter | MarketplaceFilter,
  assets: T[] | null,
  marketplaceAssets?: MarketplaceContextShape['salesResume']
) {
  return useMemo(() => {
    if (!assets) return null
    return assets
      .slice(0)
      .sort((a, b) => {
        const diff = RARITIES.indexOf(a.class) - RARITIES.indexOf(b.class)

        if (!('currency' in filter) || !marketplaceAssets) return diff
        else {
          const fallback =
            filter.order === MarketplaceOrderFilter.PRICE_ASC
              ? Number.POSITIVE_INFINITY
              : Number.NEGATIVE_INFINITY
          const salesResumeForAsset =
            marketplaceAssets[a.id]?.price[filter.currency!]?.lowest || fallback
          const salesResumeForAsset2 =
            marketplaceAssets[b.id]?.price[filter.currency!]?.lowest || fallback
          switch (filter.order) {
            case MarketplaceOrderFilter.PRICE_ASC:
              return salesResumeForAsset - salesResumeForAsset2
            case MarketplaceOrderFilter.PRICE_DES:
              return -(salesResumeForAsset - salesResumeForAsset2)
            default:
              return diff
          }
        }
      })
      .filter((asset) => (filter.rarity ? asset.class === filter.rarity : true))
      .filter((asset) =>
        filter.trait
          ? asset.attributes.filter(
              (trait) =>
                trait.trait_type in filter.trait! &&
                trait.value === filter.trait![trait.trait_type]
            ).length === Object.keys(filter.trait).length
          : true
      )
      .filter((asset) => {
        if (
          'currency' in filter &&
          filter.asset === MarketplaceAssetFilter.ONLY_ON_SALE
        ) {
          const salesResumeForAsset = marketplaceAssets?.[asset.id]
          return (
            !!salesResumeForAsset && salesResumeForAsset.price[filter.currency!]
          )
        } else return true
      })
  }, [filter, assets, marketplaceAssets])
}

export function useAssetFilter<F extends Filter | MarketplaceFilter>(
  filterInit: F
) {
  const [filter, updateFilter] = useState<typeof filterInit>({
    ...filterInit,
  })

  const handleField = useCallback(
    (field: keyof MarketplaceFilter) => (filter: string) => {
      updateFilter((prev) => ({ ...prev, [field]: filter }))
    },
    []
  )

  const handleNoFilter = useCallback(() => {
    updateFilter(() => ({ ...filterInit }))
  }, [])

  const handleTextFilter = useMemo(
    () =>
      debounce((text: string) => {
        updateFilter((prev) => ({ ...prev, open: text }))
      }, 500),
    []
  )

  const handleTraitFilter = useCallback(
    (key: string, _value: string | unknown) => {
      if (!_value) {
        updateFilter((prev) => {
          const _traitFilter = { ...prev.trait }
          delete _traitFilter[key]
          return {
            ...prev,
            trait: {
              ..._traitFilter,
            },
          }
        })
        return
      }

      const value: string = typeof _value === 'string' ? _value : ''
      updateFilter((prev) => ({
        ...prev,
        trait: {
          ...prev.trait,
          [key]: value,
        },
      }))
    },
    []
  )

  const controls = useMemo(
    () => ({
      onRarityFilter: handleField('rarity'),
      onCurrencyFilter: handleField('currency'),
      onAssetFilter: handleField('asset'),
      onOrderFilter: handleField('order'),
      onTraitFilter: handleTraitFilter,
      onNoFilter: handleNoFilter,
      onTextFilter: handleTextFilter,
    }),
    [handleField, handleTraitFilter, handleNoFilter]
  )

  return [filter as F, controls] as const
}

function useUnknownFilterLog(traitFilters: V1Options['traitFilters']) {
  const traitNames = Object.keys(traitFilters)
  useMemo(() => {
    for (let traitName of traitNames) {
      if (
        ATTRIBUTES_VISIBILITY.hidden.includes(traitName) ||
        ATTRIBUTES_VISIBILITY.visible.includes(traitName)
      )
        return
      logDevError(
        `The trait "${traitName}" is not set as visible nor hidden, showing it, but please, review it`,
        module
      )
    }
  }, [traitNames.join('')])
}

export function useFilterOptionsFromAssetsList(assets?: AssetType[]) {
  const traitFilters: { [key: string]: OptionsShape[] } = {}
  assets?.forEach((asset: AssetType) =>
    asset?.attributes.forEach((trait: TraitType) => {
      if (ATTRIBUTES_VISIBILITY.hidden.includes(trait.trait_type)) return
      trait.trait_type in traitFilters
        ? !traitFilters[trait.trait_type].some(
            (opt) => opt.value === trait.value
          ) &&
          traitFilters[trait.trait_type].push({
            label: trait.value,
            value: trait.value,
          })
        : (traitFilters[trait.trait_type] = [
            { label: trait.value, value: trait.value },
          ])
    })
  )
  useUnknownFilterLog(traitFilters)
  return {
    traitFilters,
  }
}

export type V1Options = ReturnType<typeof useFilterOptionsFromAssetsList>
export function useFilterOptionsFromFilterGraph(
  filterType: 'marketplace'
): V1Options {
  const relatedGallery = useRouteGalleryOrDefault()
  const filter = useFirestoreWatch(getFilterSnapshot, [
    relatedGallery?.id!,
    filterType,
  ])
  const traitFilters = useMemo<V1Options['traitFilters']>(() => {
    if (!filter || !filter.at(0)) return {}
    else {
      return filter[0].attributes.reduce((acc, _attr, idx) => {
        if (ATTRIBUTES_VISIBILITY.hidden.includes(_attr)) return acc
        acc[_attr] = getKeysAtLevel(filter[0].graph, idx + 1).map((a) => ({
          label: a,
          value: a,
        }))

        return acc
      }, {} as V1Options['traitFilters'])
    }
  }, [filter, filter?.length])

  useUnknownFilterLog(traitFilters)

  return {
    traitFilters,
  }
}
